import { FC } from 'react'
import { useObservable } from 'react-use'

import {
  Bag as BagIcon,
  ViewportSizes,
  useIsBrowser,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useCartDialog } from '../../../../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../../../../app/_providers/ExperimentationProvider.client'
import { trackEvent } from '../../../../../lib/analytics'
import { useShowStickyAddToBag } from '../../../../../lib/hooks/useShowStickyAddToBag'
import { AddToBagContainer } from '../contexts/AddToBagContainer'
import { ProductDetailsContainer } from '../contexts/ProductDetailsContainer'
import { useIsGiftCard } from '../hooks/useIsGiftCard'

import { AddToBagButton } from './AddToBagButton'
import { GarmentAttributes } from './GarmentAttributes'
import GarmentAttributeTabs from './GarmentAttributeTabs'
import GarmentDetails from './GarmentDetails'
import { GiftCardDetails } from './GiftCardDetails'
import { StickyAddToBagModal } from './StickyAddToBagModal'
import { Content, Layout } from './styles'

const STICKY_CART_VIEWPORTS = [ViewportSizes.small]

export const DetailsSection: FC = () => {
  const isBrowser = useIsBrowser()
  const isValidStickyCartViewport = useIsViewport(STICKY_CART_VIEWPORTS)
  const { isOpen: isCartDialogOpen } = useCartDialog()
  const showStickyAddToBag$ = useShowStickyAddToBag()

  const { onAddToBagClick, isAddToBagDisabled, isAddToCartInFlight, addToBagCta } =
    AddToBagContainer.useContainer()

  const { productGroup, isProductOnWaitlist } = ProductDetailsContainer.useContainer()

  const showStickyAddToBag = useObservable(showStickyAddToBag$, false)

  const isGiftCard = useIsGiftCard({
    productGroupHandle: productGroup.handle ?? '',
  })

  const addToBagCtaIconTestDecision = useFeatureFlag<
    'atb-cta-bag-icon-test-control' | 'atb-cta-bag-icon-test-variant'
  >({
    key: 'atb-cta-bag-icon-test',
    controlVariant: 'atb-cta-bag-icon-test-control',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    skip: isProductOnWaitlist,
  })

  const isIconVisible =
    addToBagCtaIconTestDecision === 'atb-cta-bag-icon-test-variant' &&
    !isAddToCartInFlight &&
    !isProductOnWaitlist

  return (
    <Layout {...trackEvent({ category: 'product details', action: 'click product details' })}>
      <Content>
        {isGiftCard ? (
          <GiftCardDetails />
        ) : (
          <GarmentDetails
            // Render the portal-specific tabbed attributes if portal color groups exists
            attributes={
              productGroup.portalColorGroups && productGroup.portalColorGroups.length > 0 ? (
                <GarmentAttributeTabs />
              ) : (
                <GarmentAttributes />
              )
            }
          />
        )}
      </Content>
      {isValidStickyCartViewport && isBrowser ? (
        <StickyAddToBagModal
          shouldDisplayStickyAddToBag={!!showStickyAddToBag && !isCartDialogOpen}
        >
          <AddToBagButton
            onClickAddToBag={onAddToBagClick}
            icon={isIconVisible && <BagIcon height={26} width={24} />}
            isProcessing={isAddToCartInFlight}
            isDisabled={isAddToBagDisabled}
            isWaitlist={isProductOnWaitlist}
            cta={addToBagCta}
          />
        </StickyAddToBagModal>
      ) : null}
    </Layout>
  )
}
